import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { CheckOutlined, ExclamationCircleFilled, QuestionCircleOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Switch, DatePicker, Radio, Tooltip, Row, Col, Checkbox } from 'antd';
import { bookingJourneyType } from 'common/constants/booking';
import TenancyDetails from '~components/tenancy-details';
import AvailabilityAndPrice from '~components/availability-and-price';
import { isLandlordRole } from '~helpers/auth';
import useListingModal from '~pages/pms-dashboard/properties/listing-management/listing-detail/listing-modal/useListingModal';
import { getSafeHtml } from 'common/helpers/safeHtml';

const RadioGroup = Radio.Group;

const ListingForm = ({ listing, form, property, roomData, type }) => {
  const { t } = useTranslation('cms');
  const { getFieldDecorator, getFieldValue } = form;
  const liveOnContainer = useRef(null);
  const liveUntilContainer = useRef(null);
  const landlord = get(property, 'landlord');
  const isManual = get(property, 'bookingJourney') === 'MANUAL';

  const { disabledStartDate, disabledEndDate, liveUntilChange } = useListingModal({
    form,
    listing,
  });

  useEffect(() => {
    const isPlaceHolder = getFieldValue('placeHolder');
    const tenancyValue = listing?.tenancyLengthValue?.split('-');

    if (isPlaceHolder && !isManual) {
      form.resetFields([
        'moveIn',
        'moveInType',
        'moveOutType',
        'tenancyLengthType',
        'moveOut',
        'tenancyLengthValueMin',
        'tenancyLengthValueMax',
        'availableThoughMoveInDatePassed',
      ]);
    }

    if (!isPlaceHolder) {
      form.setFieldsValue({
        moveInType: listing?.moveInType,
        moveOutType: listing?.moveOutType,
        tenancyLengthType: listing?.tenancyLengthType,
      });
    }

    if (!isPlaceHolder && listing?.moveIn && listing?.moveOut) {
      form.setFieldsValue({
        moveIn: moment(listing?.moveIn),
        moveOut: moment(listing?.moveOut),
        tenancyLengthValueMin: tenancyValue?.[0],
        tenancyLengthValueMax: tenancyValue?.[1],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getFieldValue('placeHolder')]);

  const handleWaitingListChange = currentValue => {
    // open wishlist and isManual, reset availability.
    if (isManual && currentValue) {
      form.setFieldsValue({
        availability: null,
      });
    }
    if (!currentValue && listing.type === 'PLACEHOLDER') {
      // close wishlist, but wishlist is actually open before (not local edit), reset availability.
      form.setFieldsValue({
        availability: null,
      });
    }
  };

  return (
    <div>
      <Form layout="inline">
        <div className="listing-form-modal__container">
          <div className="listing-form-modal__content-container">
            <h4 className="listing-form-modal__type-title">
              {t(`cms.properties.edit.rooms.listing_modal.${type}`)}
              <span className="listing-form-modal__line" />
              {roomData && roomData.name}
            </h4>
            <div className={classNames('listing-form-modal__placeholder-container')}>
              <span className="listing-form-modal__placeholder">
                {t('cms.listing.modal.place_holder.label')}
              </span>
              <If condition={property.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC}>
                <Tooltip
                  overlayStyle={{ minWidth: 300 }}
                  title={t('cms.properties.edit.rooms.listing_modal.infotips')}
                >
                  <ExclamationCircleFilled
                    style={{
                      width: '16px',
                      height: '16px',
                      color: '#faad14',
                      marginLeft: '8px',
                    }}
                  />
                </Tooltip>
              </If>
              <Form.Item className="listing-form-modal__place-holder-item">
                {getFieldDecorator('placeHolder', {
                  valuePropName: 'checked',
                })(
                  <Switch onChange={handleWaitingListChange} checkedChildren={<CheckOutlined />} />,
                )}
              </Form.Item>
            </div>

            {/* Tenancy Details */}
            <TenancyDetails
              form={form}
              billingCycle={property.billingCycle}
              listing={listing}
              showPlaceHolder={getFieldValue('placeHolder')}
              t={t}
              isInstantBook={get(property, 'bookingJourney') === 'INSTANT_BOOK'}
              isManual={get(property, 'bookingJourney') === 'MANUAL'}
              type={type}
            />

            {/* Availability and Price */}
            <AvailabilityAndPrice
              form={form}
              inventoryType={roomData?.inventoryType}
              currency={property.currency}
              showPlaceHolder={getFieldValue('placeHolder')}
              isLongtail={property.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC}
              isInstantBook={get(property, 'bookingJourney') === 'INSTANT_BOOK'}
              isManual={get(property, 'bookingJourney') === 'MANUAL'}
            />

            {/* General Setting */}
            <div className="listing-form-modal__general-setting">
              <h4 className="listing-form-modal__title listing-form-modal__title--general-setting">
                {t('cms.listing.modal.general_setting.label')}
              </h4>

              {/* Live on & Live until */}
              <div className="listing-form-modal__row-container">
                <Row gutter={9}>
                  <Col span={12}>
                    <div className="listing-form-modal__column-container">
                      <label className="listing-form-modal__label listing-form-modal__label--required">
                        {t('cms.listing.modal.live_on.label')}
                        <Tooltip
                          overlayStyle={{ minWidth: 300 }}
                          title={
                            <span>{getSafeHtml(t('cms.listing.modal.live_on.label.tips'))}</span>
                          }
                        >
                          <QuestionCircleOutlined className="listing-model__tips-icon" />
                        </Tooltip>
                      </label>
                      <Form.Item>
                        <div ref={liveOnContainer}>
                          {getFieldDecorator('liveOn', {
                            rules: [
                              {
                                required: true,
                                message: t('cms.listing.modal.error_message.can_not_empty'),
                              },
                            ],
                          })(
                            <DatePicker
                              style={{ width: '100%', height: '32px', borderRadius: '2px' }}
                              getCalendarContainer={() => liveOnContainer.current}
                              format="ll"
                              disabledDate={startValue =>
                                disabledStartDate(startValue, form.getFieldValue('liveUntil'))
                              }
                            />,
                          )}
                        </div>
                      </Form.Item>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="listing-form-modal__column-container">
                      <div className="listing-form-modal__column-container--flex">
                        <label className="listing-form-modal__label listing-form-modal__label--required">
                          {t('cms.listing.modal.live_until.label')}
                          <Tooltip
                            overlayStyle={{ minWidth: 300 }}
                            title={
                              <span>
                                {getSafeHtml(t('cms.listing.modal.live_until.label.tips'))}
                              </span>
                            }
                          >
                            <QuestionCircleOutlined className="listing-model__tips-icon" />
                          </Tooltip>
                        </label>
                        <div className="listing-form-modal__open-end">
                          <div className="listing-form-modal__open-end-checkbox">
                            <Form.Item style={{ height: '24px' }}>
                              {getFieldDecorator('openEnd', { valuePropName: 'checked' })(
                                <Checkbox />,
                              )}
                            </Form.Item>
                          </div>
                          <span className="listing-form-modal__open-end-text">
                            {t('cms.listing.modal.live_until.open_end')}
                          </span>
                        </div>
                      </div>
                      <Form.Item>
                        <div ref={liveUntilContainer}>
                          {getFieldDecorator('liveUntil', {
                            rules: [
                              {
                                required: !getFieldValue('openEnd'),
                                message: t('cms.listing.modal.error_message.can_not_empty'),
                              },
                            ],
                          })(
                            <DatePicker
                              style={{ width: '100%', height: '32px', borderRadius: '2px' }}
                              getCalendarContainer={() => liveUntilContainer.current}
                              format="ll"
                              disabledDate={endValue =>
                                disabledEndDate(getFieldValue('liveOn'), endValue)
                              }
                              disabled={getFieldValue('openEnd')}
                              placeholder={
                                getFieldValue('openEnd')
                                  ? ''
                                  : t('cms.listing.modal.live_until.placeholder')
                              }
                              onChange={liveUntilChange}
                            />,
                          )}
                        </div>
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </div>

              <If condition={!isLandlordRole()}>
                {/* Auto Price & Auto availability */}
                <div className="listing-form-modal__row-container listing-form-modal__row-container--last">
                  <Row gutter={9}>
                    <Col span={12}>
                      <div className="listing-form-modal__column-container">
                        <label className="listing-form-modal__label">
                          {t('cms.listing.modal.auto_price.label')}
                        </label>
                        <Form.Item>
                          {getFieldDecorator('autoPriceAllowed', {
                            initialValue: false,
                          })(
                            <RadioGroup disabled={landlord?.autoUpdate !== null}>
                              <Radio value>{t('cms.listing.modal.option.yes')}</Radio>
                              <Radio value={false}>{t('cms.listing.modal.option.no')}</Radio>
                            </RadioGroup>,
                          )}
                        </Form.Item>
                      </div>
                    </Col>

                    <Col span={12}>
                      <div className="listing-form-modal__column-container">
                        <label className="listing-form-modal__label">
                          {t('cms.listing.modal.auto_availability.label')}
                        </label>
                        <Form.Item>
                          {getFieldDecorator('autoAvailAllowed', {
                            initialValue: false,
                          })(
                            <RadioGroup disabled={landlord?.autoUpdate !== null}>
                              <Radio value>{t('cms.listing.modal.option.yes')}</Radio>
                              <Radio value={false}>{t('cms.listing.modal.option.no')}</Radio>
                            </RadioGroup>,
                          )}
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                </div>
              </If>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};

ListingForm.propTypes = {
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    resetFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
    validateFieldsAndScroll: PropTypes.func.isRequired,
  }).isRequired,
  listing: PropTypes.object,
  roomData: PropTypes.object,
  property: PropTypes.object,
  type: PropTypes.string,
};

ListingForm.defaultProps = {
  listing: null,
  onChooseNextListing: () => {},
  setListingsDataChanged: () => {},
  roomData: {},
  property: {},
  type: 'create',
};

export default ListingForm;
